import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotificationBell } from '@speles7172/message-console';
import '@speles7172/message-console/styles.css';
import { useAuth } from '../../context/AuthContext';
import { messageTransport } from '../../api/notifications';
import { EntityModal } from '../common/EntityModal';
import { ConversationView } from './ConversationView';
import { notificationPath } from './notificationPath';

/**
 * The bell, and the conversations behind it (COMMUNICATIONS-DESIGN D12).
 *
 * ⚠️ **The Inquiries tab was already built and switched off.** The package's
 * bell has always had two tabs, and turns the conversations one on when it is
 * given both `transport.threads` and `onOpenThread`. We passed the transport
 * and never the handler, so every deployment has shown a notifications-only
 * dropdown while the other half sat in the bundle. The fix was a prop, not a
 * feature.
 *
 * ⚠️ **`placement="up-start"`, because this lives at the foot of the sidebar.**
 * The default opens downward from a control already at the bottom of the
 * viewport: the panel hangs below the fold and the document grows a second
 * scrollbar beside the panel's own. Nothing errors — it just looks broken.
 *
 * ⚠️ **The bell is the package's, wrapped in `.peles-controls`.** Re-drawing a
 * dropdown, its badge, its polling and its read-state in Tailwind would be
 * re-implementing the part that was worth adopting. The wrapper is Phase A's
 * palette bridge, so it wears this application's purple.
 *
 * ⚠️ **It loads its list only once opened.** A bell is on every page, and
 * fetching twenty rows to render a number would be a request per page view —
 * the count alone is polled.
 *
 * ⚠️ **The thread opens in a modal rather than on a page.** The bell is on
 * every screen, and navigating away to read a two-line reply loses whatever the
 * reader was in the middle of — a half-filled expense form, a floor plan mid-
 * drag. Closing the modal puts them back exactly where they were.
 */
export function ConversationsBell({ className }: { className?: string }) {
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!user) return null;

  return (
    <div className={`peles-controls ${className ?? ''}`} data-testid="conversations-bell">
      <NotificationBell
        transport={messageTransport}
        pollMs={60_000}
        placement="up-start"
        onSeeAll={() => navigate('/notifications')}
        onOpen={(notification) => {
          const path = notificationPath(notification);
          if (path) navigate(path);
        }}
        // The prop that turns the conversations tab on.
        onOpenThread={(threadId) => setOpenThreadId(threadId)}
      />

      <EntityModal
        open={openThreadId !== null}
        onClose={() => setOpenThreadId(null)}
        title="Conversation"
        size="lg"
      >
        {openThreadId && <ConversationView threadId={openThreadId} />}
      </EntityModal>
    </div>
  );
}
